"use client";

import React, { useEffect, useState } from "react";
import { ChevronRight, User } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn-components/ui/popover";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { cn } from "@/lib/utils";
import { useIsAdmin, useVybeAuthStore } from "@/lib/vybe-auth";
import { crmBaseUrl, mintPortalHandoff } from "@/lib/vybe-auth/api";
import { GoogleSignInButton } from "@/components/common/GoogleSignInButton";

/**
 * Open a URL in the external browser. In the desktop (Tauri) shell an external http(s)
 * URL is handed to the OS default browser; on web/mobile it opens a new tab. `noopener`
 * severs the reverse `window.opener` handle for safety.
 */
function openExternal(url: string): void {
  if (!url) return;
  window.open(url, "_blank", "noopener");
}
import {
  useAiCredits,
  isUnlimited,
  formatUtcResetCountdown,
  type AiCredits,
} from "@/lib/agent/ai-credits";

/** "4h 12m" style session age from a start timestamp. */
function sessionAge(startedAt: number | null): string {
  if (!startedAt) return "—";
  const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function initials(name?: string | null, email?: string): string {
  const source = (name && name.trim()) || email || "";
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Session-aware user chip for the terminal header. Blue when authenticated & presence-connected, grey
 * when signed out. Click → popover: a login form (request code / verify code) when logged out, or the
 * signed-in identity card (name, email, LLM plan, presence status, session age, Sign out) when logged in.
 */
export function HeaderUserChip() {
  const status = useVybeAuthStore((s) => s.status);
  const user = useVybeAuthStore((s) => s.user);
  const token = useVybeAuthStore((s) => s.token);
  const presenceConnected = useVybeAuthStore((s) => s.presenceConnected);
  const sessionStartedAt = useVybeAuthStore((s) => s.sessionStartedAt);
  const error = useVybeAuthStore((s) => s.error);
  const pendingEmail = useVybeAuthStore((s) => s.pendingEmail);
  const requestCode = useVybeAuthStore((s) => s.requestCode);
  const verifyCode = useVybeAuthStore((s) => s.verifyCode);
  const logout = useVybeAuthStore((s) => s.logout);
  const { credits, refresh: refreshCredits } = useAiCredits();

  const signedIn = !!token;
  // Client-portal (CRM) origin, same config accessor the auth API already uses. Empty
  // when unconfigured (e.g. a bare local build) — in that case we hide the button.
  const portalUrl = crmBaseUrl().replace(/\/$/, "");
  const portalHost = portalUrl.replace(/^https?:\/\//, "");
  const codeSent = status === "code-sent";
  const active = signedIn && presenceConnected;
  // Signed in but the presence socket isn't live — surface it as a warning (orange chip),
  // not a silent grey. Presence should always be connected whenever a session exists.
  const presenceWarn = signedIn && !presenceConnected;
  // Staff identity, straight off the presence identity echo. Note: AI access is NOT
  // role-gated — the LLM server (`GET /api/agent/credits`) issues a daily budget to
  // every signed-in user, including the Free plan. So the usage meter renders off the
  // credits snapshot alone, exactly like the agent-sidebar meter does.
  const isAdmin = useIsAdmin();

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  // Reverse SSO (proposal 52): mint a single-use handoff code from the bearer token, then open the
  // portal's cookie-side redeem URL — the CRM signs in without an OTP. Any failure (offline, expired
  // session, older CRM) gracefully falls back to the plain portal link.
  const openPortal = async () => {
    if (!portalUrl) return;
    if (!token) {
      openExternal(portalUrl);
      return;
    }
    setPortalBusy(true);
    try {
      const code = await mintPortalHandoff(token);
      openExternal(`${portalUrl}/auth/portal?code=${encodeURIComponent(code)}`);
    } catch {
      openExternal(portalUrl);
    } finally {
      setPortalBusy(false);
    }
  };
  const [, forceTick] = useState(0);

  // Re-render every 30s while the card is open so the session age stays fresh.
  useEffect(() => {
    if (!open || !signedIn) return;
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [open, signedIn]);

  // Fetch the live daily-credit snapshot when the popover opens. The hook caches
  // the result; we just re-peek on each open so the meter reflects recent spend.
  useEffect(() => {
    if (open && signedIn) void refreshCredits();
  }, [open, signedIn, refreshCredits]);

  const submit = async () => {
    setBusy(true);
    try {
      if (codeSent) await verifyCode(code);
      else await requestCode(email);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-6 w-6 p-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            active
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : presenceWarn
                ? "bg-orange-500 text-white hover:bg-orange-600"
                : "text-muted-foreground",
          )}
          title={
            presenceWarn
              ? `Signed in — presence offline (reconnecting…) — ${user?.email ?? ""}`
              : signedIn
                ? `Signed in — ${user?.email ?? ""}`
                : "Sign in to Vybe Trader"
          }
          aria-label="Vybe account"
        >
          <User className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        {signedIn ? (
          <div>
            <div className="flex items-center gap-2.5 border-b border-border px-3 py-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-bid-blue to-orange-500 text-[11px] font-bold text-black">
                {initials(user?.displayName, user?.email)}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-xs font-semibold">
                    {user?.displayName || user?.email}
                  </span>
                  <PlanPill plan={credits?.plan ?? user?.plan} />
                  {isAdmin ? <AdminBadge /> : null}
                </div>
                <div className="truncate text-[10px] text-muted-foreground">
                  {user?.email}
                </div>
              </div>
            </div>
            <div className="space-y-1.5 px-3 py-2.5 text-xs">
              <UsageTile credits={credits} />
              <Row label="presence">
                <span
                  className={cn(
                    "font-medium",
                    presenceConnected ? "text-green-500" : "text-orange-500",
                  )}
                >
                  ● {presenceConnected ? "connected" : "offline"}
                </span>
              </Row>
              {presenceWarn ? (
                <p className="text-[10px] leading-tight text-orange-500">
                  You're signed in but the presence connection is down — retrying
                  automatically.
                </p>
              ) : null}
              <Row label="session">{sessionAge(sessionStartedAt)}</Row>
              {portalUrl ? (
                <Button
                  size="sm"
                  className="mt-1.5 h-7 w-full justify-between gap-2 bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700"
                  disabled={portalBusy}
                  onClick={() => {
                    void openPortal().finally(() => setOpen(false));
                  }}
                  title="Open the client portal in your browser — stays signed in"
                >
                  <span className="inline-flex items-center gap-1.5">
                    {portalBusy ? "Opening…" : "Open client portal"}{" "}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                  <span className="truncate font-mono text-[9px] font-normal opacity-75">
                    {portalHost}
                  </span>
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="mt-1.5 h-7 w-full text-xs text-destructive hover:text-destructive"
                onClick={() => {
                  void logout();
                  setOpen(false);
                }}
              >
                Sign out
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 p-3">
            <div className="space-y-0.5">
              <div className="text-xs font-semibold">Sign in to Vybe Trader</div>
              <p className="text-[11px] text-muted-foreground">
                {codeSent
                  ? `Enter the 6-digit code sent to ${pendingEmail}.`
                  : "Trading works without signing in."}
              </p>
            </div>
            {/* Google door (proposal 71) — only on the first step; once a code is out, finish that. */}
            {!codeSent ? <GoogleSignInButton compact /> : null}

            {!codeSent ? (
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void submit()}
                className="h-8 text-xs"
              />
            ) : (
              <Input
                inputMode="numeric"
                maxLength={6}
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void submit()}
                className="h-8 text-center text-xs tracking-[0.4em]"
              />
            )}
            {error ? (
              <div className="text-[11px] text-destructive">{error}</div>
            ) : null}
            <Button
              size="sm"
              className="h-8 w-full text-xs"
              disabled={
                busy ||
                (codeSent ? code.trim().length === 0 : email.trim().length === 0)
              }
              onClick={() => void submit()}
            >
              {busy
                ? "Please wait…"
                : codeSent
                  ? "Verify & continue"
                  : "Email me a code"}
            </Button>
            {codeSent ? (
              <Button
                variant="link"
                size="sm"
                className="h-6 w-full text-[11px] text-muted-foreground"
                onClick={() => void requestCode(pendingEmail ?? email)}
              >
                Resend code
              </Button>
            ) : null}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{children}</span>
    </div>
  );
}

/** FREE / PRO / DESK pill using the brand bid-blue accent (design-system token). */
function PlanPill({ plan }: { plan?: string | null }) {
  const label = (plan ?? "free").toUpperCase();
  return (
    <span className="inline-flex items-center rounded-[9px] border border-bid-blue bg-bid-blue/10 px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.5px] text-bid-blue">
      {label}
    </span>
  );
}

/** Red ADMIN badge for staff identities (roles.includes("Admin")). */
function AdminBadge() {
  return (
    <span className="inline-flex items-center rounded-lg border border-red-500/50 bg-red-500/10 px-1.5 py-0.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.5px] text-red-500">
      Admin
    </span>
  );
}

/**
 * Daily Vybe Agent credit meter for the account card: `used / limit` with a `.meter`-style
 * fill bar and a "resets 00:00 UTC" note. Desk (unlimited) renders ∞ and suppresses the bar.
 * The count is per-request, hence the "requests" label.
 */
function UsageTile({ credits }: { credits: AiCredits | null }) {
  const [countdown, setCountdown] = useState(() => formatUtcResetCountdown());
  useEffect(() => {
    const id = setInterval(() => setCountdown(formatUtcResetCountdown()), 30_000);
    return () => clearInterval(id);
  }, []);

  const unlimited = isUnlimited(credits);
  const used = credits?.used ?? 0;
  const limit = credits?.limit ?? 0;
  const remaining = credits?.remaining ?? 0;
  const pct = !unlimited && limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const fmt = (n: number) => n.toLocaleString();

  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-border bg-muted/40 px-2.5 py-2">
      <div className="flex items-center justify-between font-mono text-[10.5px]">
        <span className="uppercase tracking-[0.08em] text-muted-foreground">
          Vybe Agent · today
        </span>
        <span className="text-foreground">
          {unlimited ? (
            <b className="font-semibold text-orange-500">∞</b>
          ) : (
            <>
              <b className="font-semibold text-orange-500">{fmt(used)}</b> /{" "}
              {credits ? fmt(limit) : "—"}
            </>
          )}
        </span>
      </div>
      {!unlimited ? (
        <div className="h-2 overflow-hidden rounded-md border border-border bg-muted">
          <span
            className="block h-full rounded-md"
            style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg, #4B9BFF, #FF6B35)",
            }}
          />
        </div>
      ) : null}
      <div className="font-mono text-[9.5px] text-muted-foreground">
        {unlimited
          ? "unlimited requests · resets 00:00 UTC"
          : `${credits ? fmt(remaining) : "—"} requests left · resets 00:00 UTC · in ${countdown}`}
      </div>
    </div>
  );
}
